import React, { createContext, useContext, useState, useCallback } from 'react';

type DatePreset = 'today' | 'week' | 'month' | '3months' | 'custom';

interface FilterState {
  datePreset: DatePreset;
  dateFrom: string;
  dateTo: string;
  selectedState: string;
  selectedDistrict: string;
  selectedDISCOM: string;
  selectedCircle: string;
  selectedSubDivision: string;
  selectedFeeder: string;
  activeOnly: boolean;
  faultsOnly: boolean;
  prThreshold: number;
  underperformersOnly: boolean;
  statusFilter: string;
}

interface FilterContextValue extends FilterState {
  setDatePreset: (preset: DatePreset) => void;
  setSelectedState: (state: string) => void;
  setSelectedDistrict: (district: string) => void;
  setSelectedDISCOM: (discom: string) => void;
  setSelectedCircle: (circle: string) => void;
  setSelectedSubDivision: (val: string) => void;
  setSelectedFeeder: (feeder: string) => void;
  setActiveOnly: (val: boolean) => void;
  setFaultsOnly: (val: boolean) => void;
  setPRThreshold: (val: number) => void;
  setUnderperformersOnly: (val: boolean) => void;
  setStatusFilter: (val: string) => void;
  resetFilters: () => void;
}

const defaultState: FilterState = {
  datePreset: 'today',
  dateFrom: '2026-04-27',
  dateTo: '2026-04-27',
  selectedState: '',
  selectedDistrict: '',
  selectedDISCOM: '',
  selectedCircle: '',
  selectedSubDivision: '',
  selectedFeeder: '',
  activeOnly: false,
  faultsOnly: false,
  prThreshold: 70,
  underperformersOnly: false,
  statusFilter: 'all',
};

const FilterContext = createContext<FilterContextValue | undefined>(undefined);

export function FilterProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FilterState>(defaultState);

  const setDatePreset = useCallback((preset: DatePreset) => {
    setState(s => ({ ...s, datePreset: preset }));
  }, []);

  const setSelectedState = useCallback((selectedState: string) => {
    setState(s => ({ ...s, selectedState, selectedDistrict: '', selectedDISCOM: '', selectedCircle: '', selectedFeeder: '' }));
  }, []);

  const setSelectedDistrict = useCallback((selectedDistrict: string) => {
    setState(s => ({ ...s, selectedDistrict, selectedDISCOM: '', selectedCircle: '', selectedFeeder: '' }));
  }, []);

  const setSelectedDISCOM = useCallback((selectedDISCOM: string) => {
    setState(s => ({ ...s, selectedDISCOM, selectedSubDivision: '', selectedCircle: '', selectedFeeder: '' }));
  }, []);

  const setSelectedCircle = useCallback((selectedCircle: string) => {
    setState(s => ({ ...s, selectedCircle, selectedSubDivision: '', selectedFeeder: '' }));
  }, []);

  const setSelectedSubDivision = useCallback((selectedSubDivision: string) => {
    setState(s => ({ ...s, selectedSubDivision }));
  }, []);

  const setSelectedFeeder = useCallback((selectedFeeder: string) => {
    setState(s => ({ ...s, selectedFeeder }));
  }, []);

  const setActiveOnly = useCallback((activeOnly: boolean) => {
    setState(s => ({ ...s, activeOnly }));
  }, []);

  const setFaultsOnly = useCallback((faultsOnly: boolean) => {
    setState(s => ({ ...s, faultsOnly }));
  }, []);

  const setPRThreshold = useCallback((prThreshold: number) => {
    setState(s => ({ ...s, prThreshold }));
  }, []);

  const setUnderperformersOnly = useCallback((underperformersOnly: boolean) => {
    setState(s => ({ ...s, underperformersOnly }));
  }, []);

  const setStatusFilter = useCallback((statusFilter: string) => {
    setState(s => ({ ...s, statusFilter }));
  }, []);

  const resetFilters = useCallback(() => {
    setState(defaultState);
  }, []);

  return (
    <FilterContext.Provider value={{
      ...state,
      setDatePreset,
      setSelectedState,
      setSelectedDistrict,
      setSelectedDISCOM,
      setSelectedCircle,
      setSelectedSubDivision,
      setSelectedFeeder,
      setActiveOnly,
      setFaultsOnly,
      setPRThreshold,
      setUnderperformersOnly,
      setStatusFilter,
      resetFilters,
    }}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilter(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilter must be used inside FilterProvider');
  return ctx;
}
